import React from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from '../data/supabase';

interface UserSummary {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  profile_id?: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type UserId = string | null | undefined;

const EMPTY_USER_MAP: Record<string, UserSummary> = {};

export function useUserIdMap(ids: UserId[], options?: { supabase?: SupabaseClient; /** When this changes (e.g. profile query data), re-fetch so Created by / Updated by resolve after async loads. */ refreshTrigger?: unknown }) {
  const [map, setMap] = React.useState<Record<string, UserSummary>>(EMPTY_USER_MAP);

  React.useEffect(() => {
    let active = true;

    (async () => {
      const unique = Array.from(new Set(ids.filter((val): val is string => !!val)));
      const client = options?.supabase ?? getSupabaseClient();
      if (!unique.length) {
        if (active) setMap(EMPTY_USER_MAP);
        return;
      }

      const next: Record<string, UserSummary> = {};
      const resolved = new Set<string>();
      const uuidCandidates = unique.filter((id) => UUID_RE.test(id));

      if (uuidCandidates.length) {
        const { data: authMatches, error: authErr } = await client
          .from('user_profiles')
          .select('id, auth_user_id, full_name, first_name, last_name, email')
          .in('auth_user_id', uuidCandidates);
        if (!active) return;
        if (!authErr) {
          for (const row of (authMatches || []) as Array<{ id?: string | null; auth_user_id?: string | null; full_name?: string | null; first_name?: string | null; last_name?: string | null; email?: string | null; }>) {
            const authId = row?.auth_user_id;
            if (!authId) continue;
            resolved.add(authId);
            next[authId] = {
              full_name: row?.full_name ?? null,
              first_name: row?.first_name ?? null,
              last_name: row?.last_name ?? null,
              email: row?.email ?? null,
              profile_id: row?.id ?? null,
            };
          }
        }
      }

      const remaining = unique.filter((id) => !resolved.has(id));
      const remainingUuid = remaining.filter((id) => UUID_RE.test(id));

      if (remainingUuid.length) {
        const { data: profileMatches, error: profileErr } = await client
          .from('user_profiles')
          .select('id, full_name, first_name, last_name, email')
          .in('id', remainingUuid);
        if (!active) return;
        if (!profileErr) {
          for (const row of (profileMatches || []) as Array<{ id?: string | null; full_name?: string | null; first_name?: string | null; last_name?: string | null; email?: string | null; }>) {
            if (!row?.id) continue;
            next[row.id] = {
              full_name: row?.full_name ?? null,
              first_name: row?.first_name ?? null,
              last_name: row?.last_name ?? null,
              email: row?.email ?? null,
              profile_id: row?.id ?? null,
            };
          }
        }
      }

      if (active) setMap(next);
    })();

    return () => {
      active = false;
    };
  }, [options?.supabase, JSON.stringify(Array.from(new Set(ids.filter((val): val is string => !!val))).sort()), options?.refreshTrigger]);

  return map;
}

export function formatUserSummary(map: Record<string, UserSummary>, id?: string | null): string | null {
  if (!id) return null;
  const summary = map[id];
  const fullName = summary?.full_name?.trim();
  if (fullName) return fullName;
  const composed = `${summary?.first_name ?? ''} ${summary?.last_name ?? ''}`.trim();
  if (composed) return composed;
  if (summary?.email) return String(summary.email);
  return `${id.slice(0, 8)}…`;
}
